import { useRef, type DetailedHTMLProps, type KeyboardEvent, type TextareaHTMLAttributes } from "react";
import { useTranslation } from "react-i18next";

import { CircleButton } from "./CircleButton.tsx";
import { IconButton } from "./IconButton.tsx";
import { Textarea } from "./Textarea.tsx";

const PREFIX_WS = /^\s+/u;
const IS_MAC = /\bMacintosh\b/iu;
const URL = /^\w+:\/\//u;
const INDENT = /^(\t|\s{0,4})/u;
const TABLE = `|    |    |\n| :- | :- |\n|    |    |\n`;

function startSelectedLines({ value, selectionStart, selectionEnd }: HTMLTextAreaElement) {
  const lineStarts = [];
  for (let position = selectionEnd; ;) {
    position =
      value.charAt(position) === "\n" ? value.lastIndexOf("\n", position - 1) : value.lastIndexOf("\n", position);
    lineStarts.push(position + 1);
    if (position === -1 || position <= selectionStart) break;
  }
  return lineStarts;
}

export function RichTextarea(
  props: DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>,
) {
  const { t } = useTranslation("rich-text");
  const textarea = useRef<HTMLTextAreaElement>(null);

  function toggleSurround(prefix: string, suffix: string = prefix, offset?: number, preserve = true) {
    offset = offset ?? prefix.length; // oxlint-disable-line no-param-reassign, logical-assignment-operators
    if (!textarea.current) return;
    textarea.current.focus();
    const { value, selectionStart, selectionEnd } = textarea.current;
    let matchPrefix = null;
    let matchSuffix = null;
    for (let i = selectionStart - prefix.length; i <= Math.min(selectionStart, selectionEnd - prefix.length); i += 1) {
      if (value.slice(i, i + prefix.length) === prefix) {
        matchPrefix = i;
        break;
      }
    }
    for (let i = selectionEnd; i >= Math.max(selectionStart, selectionEnd - suffix.length); i -= 1) {
      if (value.slice(i, i + suffix.length) === suffix) {
        matchSuffix = i;
        break;
      }
    }
    if (matchPrefix !== null && matchSuffix !== null) {
      textarea.current.setRangeText(
        value.slice(matchPrefix + prefix.length, matchSuffix),
        matchPrefix,
        matchSuffix + suffix.length,
      );
    } else {
      const selection = value.slice(selectionStart, selectionEnd);
      textarea.current.setRangeText(`${prefix}${selection}${suffix}`);
      if (preserve) {
        textarea.current.setSelectionRange(selectionStart + offset, selectionEnd + offset);
      } else {
        textarea.current.setSelectionRange(selectionEnd + offset, selectionEnd + offset);
      }
    }
  }

  function togglePrefix(matchPrefix: RegExp, prefix: string) {
    if (!textarea.current) return;
    textarea.current.focus();
    const { value } = textarea.current;
    const lineStarts = startSelectedLines(textarea.current);
    const minIndent = Math.min(...lineStarts.map((pos) => PREFIX_WS.exec(value.slice(pos))?.[0]?.length ?? 0));
    const positions = lineStarts.map((pos) => pos + minIndent);
    const alreadyPrefixed = positions.filter(
      (position) => matchPrefix.test(value.slice(position)) || PREFIX_WS.test(value.slice(position)),
    );
    if (alreadyPrefixed.length === positions.length) {
      for (const position of positions) {
        const match = matchPrefix.exec(value.slice(position))?.[0];
        if (match) {
          textarea.current.setRangeText("", position, position + match.length, "preserve");
        }
      }
    } else {
      for (const position of positions) {
        if (!alreadyPrefixed.includes(position) && !PREFIX_WS.test(value.slice(position))) {
          textarea.current.setRangeText(prefix, position, position, "preserve");
        }
      }
    }
  }

  function toggleBulletList() {
    togglePrefix(/^[-*+]\s/u, "- ");
  }

  function toggleNumberList() {
    togglePrefix(/^\d+\.\s/u, "1. ");
  }

  function toggleCheckList() {
    togglePrefix(/^-\s\[[x\s]\]\s/u, "- [ ] ");
  }

  function toggleQuote() {
    togglePrefix(/^>\s/u, "> ");
  }

  function unindent() {
    if (!textarea.current) return;
    textarea.current.focus();
    const { value } = textarea.current;
    const lineStarts = startSelectedLines(textarea.current);
    for (const position of lineStarts) {
      const remove = INDENT.exec(value.slice(position))?.[1]?.length ?? 0;
      textarea.current.setRangeText("", position, position + remove);
    }
  }

  function indent() {
    if (!textarea.current) return;
    textarea.current.focus();
    const lineStarts = startSelectedLines(textarea.current);
    for (const position of lineStarts) {
      textarea.current.setRangeText("    ", position, position);
    }
  }

  function insertTable() {
    if (!textarea.current) return;
    textarea.current.focus();
    const { value, selectionEnd } = textarea.current;
    const endOfLine = value.indexOf("\n", selectionEnd);
    if (endOfLine === -1) {
      textarea.current.setRangeText(`\n${TABLE}`, value.length, value.length);
      textarea.current.setSelectionRange(value.length + 3, value.length + 5);
    } else {
      textarea.current.setRangeText(TABLE, endOfLine + 1, endOfLine + 1);
      textarea.current.setSelectionRange(endOfLine + 3, endOfLine + 5);
    }
  }

  async function handleKeyboard(event: KeyboardEvent<HTMLTextAreaElement>) {
    const textarea = event.currentTarget;
    if (event.key === "Tab") {
      if (textarea.selectionStart === textarea.selectionEnd) return;
      event.preventDefault();
      if (event.shiftKey) {
        unindent();
      } else {
        indent();
      }
      return;
    }
    const isShortcut = IS_MAC.test(navigator.userAgent) ? event.metaKey : event.ctrlKey;
    if (!isShortcut) return;
    switch (event.key) {
      case "b":
        toggleSurround("**");
        event.preventDefault();
        break;
      case "i":
        toggleSurround("*");
        event.preventDefault();
        break;
      case "v": {
        event.preventDefault();
        const pasting = await navigator.clipboard.readText();
        if (!URL.test(pasting)) {
          textarea.setRangeText(pasting);
          break;
        }
        toggleSurround("[", `](${pasting})`, 4 + pasting.length, false);
        break;
      }
      default:
    }
  }

  return (
    <div className="rounded-sm bg-black/5">
      <div className="flex flex-row flex-wrap items-center p-2 pr-4">
        <IconButton aria-label={t("bold")} type="button" size={16} name="bold" onClick={() => toggleSurround("**")} />
        <IconButton
          aria-label={t("italic")}
          type="button"
          size={16}
          name="italic"
          onClick={() => toggleSurround("*")}
        />
        <CircleButton aria-label={t("strikethrough")} type="button" size={16} onClick={() => toggleSurround("~")}>
          {/* oxlint-disable-next-line jsx-no-literals */}
          <s className="decoration-2">S</s>
        </CircleButton>
        <IconButton aria-label={t("code")} type="button" size={16} name="code" onClick={() => toggleSurround("`")} />
        <IconButton
          aria-label={t("link")}
          type="button"
          size={16}
          name="link"
          onClick={() => toggleSurround("[", "]()", 3)}
        />
        <CircleButton
          aria-label={t("blockquote")}
          type="button"
          size={16}
          onClick={() => toggleQuote()}
          className="text-[16px] font-medium"
        >
          {/* oxlint-disable-next-line jsx-no-literals */}
          &ldquo; &rdquo;
        </CircleButton>
        <IconButton
          aria-label={t("bullet-list")}
          type="button"
          size={16}
          name="list"
          onClick={() => toggleBulletList()}
        />
        <CircleButton
          aria-label={t("numbered-list")}
          type="button"
          size={16}
          onClick={() => toggleNumberList()}
          className="text-[16px] font-medium"
        >
          {/* oxlint-disable-next-line jsx-no-literals */}
          1.
        </CircleButton>
        <IconButton
          aria-label={t("task-list")}
          type="button"
          size={16}
          name="check-square"
          onClick={() => toggleCheckList()}
        />
        <IconButton aria-label={t("indent")} type="button" size={16} name="arrow-left" onClick={() => unindent()} />
        <IconButton aria-label={t("unindent")} type="button" size={16} name="arrow-right" onClick={() => indent()} />
        <IconButton aria-label={t("table")} type="button" size={16} name="table" onClick={() => insertTable()} />
        <p className="ml-auto font-hint text-placeholder/70">{t("markdown-supported")}</p>
      </div>
      <Textarea contentEditable ref={textarea} {...props} onKeyDown={handleKeyboard} className="font-mono" />
    </div>
  );
}
